import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCapabilities } from "@/hooks/useCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Map, X, Save, Grid3X3, Search } from "lucide-react";

export default function AreasTab({ clientId }) {
  const [areas, setAreas] = useState([]);
  const [units, setUnits] = useState([]);
  const [subareas, setSubareas] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState("");
  const [formUnitId, setFormUnitId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [search, setSearch] = useState("");
  // Mesma decisão das rotas /areas (backend/capabilities.py) na unidade selecionada:
  // Central de Clientes disponível para a empresa, areas.<ação> efetiva e unidade no escopo.
  const caps = useCapabilities(["areas.create", "areas.edit", "areas.delete"], {
    clientId,
    unitId: selectedUnitId || null,
  });

  const loadUnits = useCallback(async () => {
    try {
      const { data } = await api.get("/units", { params: { client_id: clientId } });
      const list = (Array.isArray(data) ? data : data?.items || []).filter((u) => u.client_id === clientId);
      setUnits(list);
      if (list.length > 0 && !selectedUnitId) {
        setSelectedUnitId(list[0].id);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar unidades");
    }
  }, [clientId, selectedUnitId]);

  const loadData = useCallback(async () => {
    if (!selectedUnitId) return;
    setLoading(true);
    try {
      const [ar, sr, dr] = await Promise.all([
        api.get("/areas", { params: { unit_id: selectedUnitId } }),
        api.get("/subareas", { params: { unit_id: selectedUnitId } }),
        // Dispositivos monitorados (antes: GET /devices, os aparelhos push do
        // próprio usuário). Sem acesso ao módulo, a contagem fica oculta.
        api
          .get("/device-monitoring/devices", { params: { client_id: clientId, unit_id: selectedUnitId, limit: 1000 } })
          .catch(() => null),
      ]);
      setAreas(Array.isArray(ar.data) ? ar.data : ar.data?.items || []);
      setSubareas(Array.isArray(sr.data) ? sr.data : sr.data?.items || []);
      setDevices(dr ? (Array.isArray(dr.data) ? dr.data : dr.data?.items || []) : null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedUnitId]);

  useEffect(() => { loadUnits(); }, [loadUnits]);

  useEffect(() => { loadData(); }, [loadData]);
  const handleCreate = () => { setEditing("new"); setFormName(""); setFormUnitId(selectedUnitId || units[0]?.id || ""); };
  const handleEdit = (a) => { setEditing(a.id); setFormName(a.name || ""); setFormUnitId(a.unit_id || ""); };
  const handleCancel = () => { setEditing(null); setFormName(""); setFormUnitId(""); };

  const handleSave = async () => {
    if (!formName.trim() || !formUnitId) { toast.error("Nome e unidade sao obrigatorios"); return; }
    try {
      if (editing === "new") { await api.post("/areas", { unit_id: formUnitId, name: formName }); toast.success("Area criada"); }
      else { await api.put(`/areas/${editing}`, { unit_id: formUnitId, name: formName }); toast.success("Area atualizada"); }
      handleCancel(); loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao salvar"); }
  };

  const handleDelete = async (areaId) => {
    const count = subareas.filter((s) => s.area_id === areaId).length;
    if (count > 0) { toast.error(`Esta area possui ${count} subarea(s). Remova-as primeiro.`); return; }
    if (confirm("Excluir esta area?")) {
      try { await api.delete(`/areas/${areaId}`); toast.success("Area excluida"); loadData(); }
      catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao excluir"); }
    }
  };

  const filteredAreas = areas.filter((a) => {
    if (search.trim() && !(a.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const getUnitName = (id) => units.find((u) => u.id === id)?.name || "-";
  const getSubCount = (id) => subareas.filter((s) => s.area_id === id).length;
  const getDevCount = (id) => (devices || []).filter((d) => d.area_id === id).length;

  if (loading) return <Skeleton className="h-40 w-full" />;

  if (units.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Map className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm">Nenhuma unidade cadastrada para este cliente.</p>
        <p className="text-xs mt-1">Cadastre uma unidade primeiro para gerenciar áreas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-muted/20 p-3">
        <p className="text-xs text-muted-foreground mb-2">Selecione a unidade para visualizar as áreas:</p>
        <select value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm w-full max-w-xs" data-testid="areas-unit-select">
          <option value="">Selecione uma unidade...</option>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {!selectedUnitId ? (
        <p className="text-sm text-muted-foreground text-center py-8">Selecione uma unidade para visualizar as áreas.</p>
      ) : (
        <>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{filteredAreas.length} area(s)</Badge>
        {caps.can("areas.create") && <Button size="sm" onClick={handleCreate} disabled={!selectedUnitId} data-testid="areas-new"><Plus className="h-4 w-4 mr-1" />Nova Area</Button>}
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar area..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-sm w-48" />
        </div>
      </div>
        </>
      )}
      {editing && (
        <Card className="p-3 border-primary">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <Input autoFocus value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Nome da area" className="flex-1" data-testid="areas-form-name" />
            <select value={formUnitId} onChange={(e) => setFormUnitId(e.target.value)} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="areas-form-unit">
              <option value="">Selecione a unidade</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <Button size="sm" onClick={handleSave} data-testid="areas-form-save"><Save className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={handleCancel}><X className="h-4 w-4" /></Button>
          </div>
        </Card>
      )}
      {selectedUnitId && filteredAreas.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Nenhuma area cadastrada para esta unidade</p>
      ) : selectedUnitId ? (
        <div className="divide-y divide-border border border-border rounded-md">
          {filteredAreas.map((area) => (
            <div key={area.id} className="flex items-center justify-between p-3" data-testid={`area-row-${area.id}`}>
              <div className="flex items-center gap-3">
                <Map className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{area.name}</p>
                  <p className="text-xs text-muted-foreground">Unidade: {getUnitName(area.unit_id)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-xs"><Grid3X3 className="h-3 w-3 mr-1" />{getSubCount(area.id)} subareas</Badge>
                {devices && <Badge variant="secondary" className="text-xs">{getDevCount(area.id)} dispositivos</Badge>}
                <div className="flex gap-1">
                  {caps.can("areas.edit") && <Button size="icon" variant="ghost" onClick={() => handleEdit(area)} data-testid={`area-edit-${area.id}`}><Pencil className="h-3 w-3" /></Button>}
                  {caps.can("areas.delete") && <Button size="icon" variant="ghost" onClick={() => handleDelete(area.id)} data-testid={`area-delete-${area.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
